import { RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePersonName } from '@/entities/session/hooks/use-person-name';
import type { CollectionName } from '@/entities/shared/record/collection-name';
import { restoreRecord } from '@/entities/shared/record/restore-record';
import { EmptyState } from '@/shared/components/empty-state';
import { ListCard } from '@/shared/components/list/list-card';
import { ListRow } from '@/shared/components/list/list-row';
import { ListSkeleton } from '@/shared/components/list/list-skeleton';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatTimestamp } from '@/shared/lib/dates/format-timestamp';
import { Button } from '@/shared/ui/button/button';
import { COLLECTION_LABELS } from '../config/collection-labels';
import { useTrash } from '../hooks/use-trash';

function titleOf(data: Readonly<Record<string, unknown>>, untitled: string): string {
  return String(data.name ?? data.title ?? untitled);
}

export function TrashList() {
  const deleted = useTrash();
  const nameOf = usePersonName();
  const { t } = useTranslation();

  async function restore(collection: string, id: string) {
    // Only the app's collections are ever stored in the replica.
    await restoreRecord(collection as CollectionName, id);
    toast.success(t('Restored'));
  }

  if (!deleted) return <ListSkeleton />;
  if (deleted.length === 0) return <EmptyState icon={Trash2} title={t('The trash is empty')} />;
  return (
    <ListCard>
      {deleted.map((view) => (
        <ListRow
          key={view.id}
          title={titleOf(view.data, t('Untitled'))}
          subtitle={t('{collection} · deleted by {person} · {time}', {
            collection: t(COLLECTION_LABELS[view.collection] ?? view.collection),
            person: nameOf(view.updated_by),
            time: formatTimestamp(view.deleted_at),
          })}
          trailing={
            <Button variant="outline" size="sm" onClick={() => void restore(view.collection, view.id)}>
              <RotateCcw /> {t('Restore')}
            </Button>
          }
        />
      ))}
    </ListCard>
  );
}
